import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import InputField from './InputField';
import React from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';

// const GOLD = '#b89a5e';
const CREAM = '#e8e2d9';
const GOLD_LIGHT = '#d4b87a';
const CHARCOAL = '#1c1a17';
gsap.registerPlugin(ScrollTrigger);

// Zod validation schema
const formSchema = z.object({
  'bot-field': z.string().optional(), // Honeypot field for bots
  firstName: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(50, 'Name is too long'),
  lastName: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(50, 'Name is too long'),
  whatToBuild: z
    .string()
    .min(3, 'Text must be at least 3 characters')
    .max(500, 'Text too long'),
  howDidYouHearAboutUs: z
    .string()
    .min(3, 'Text must be at least 3 characters')
    .max(500, 'Text too long'),
  email: z.string().email('Invalid email address'),
  phone: z
    .string()
    .check(
      z.regex(/^\(?\d{3}\)?[\s-]?\d{3}-?\d{4}$/, 'Invalid phone number format')
    ),
});

type FormData = z.infer<typeof formSchema>;

interface FormExampleProps {
  isLoaded: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export default function FormExample({ isLoaded, ref }: FormExampleProps) {
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
  });
  const formRef = React.useRef<HTMLDivElement>(null);
  const formElRef = React.useRef<HTMLFormElement>(null);
  const successRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!submittedData) return;
    const form = formElRef.current;
    const success = successRef.current;
    if (!form || !success) return;

    gsap.to(form, {
      opacity: 0,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        form.style.visibility = 'hidden';
      },
    });
    gsap.fromTo(
      success,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', delay: 0.3 }
    );
  }, [submittedData]);

  React.useEffect(() => {
    if (!isLoaded) return; // wait for images to load first

    const ctx = gsap.context(() => {
      const headingEl = formRef.current?.querySelector('h2');

      gsap
        .timeline({
          scrollTrigger: {
            trigger: headingEl,
            start: 'top 85%',
            once: true,
          },
        })
        .from('.ft-word', {
          y: 44,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
          stagger: 0.07,
        });

      ScrollTrigger.refresh();
    }, formRef);

    return () => ctx.revert();
  }, [isLoaded]); // re-runs once isLoaded flips to true
  // const onSubmit = async (data: FormData) => {
  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 1000));

  //   setSubmittedData(data);
  //   console.log('Form submitted:', data);
  // };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const encode = (data: Record<string, any>) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])
      )
      .join('&');
  };
  const onSubmit = async (data: FormData) => {
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact', // Must match the form name attribute
          ...data,
        }),
      });
      setSubmittedData(data);
      reset();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // submission error
    }
  };

  return (
    <div
      id="contact"
      ref={formRef}
      className="bg-gray-50 flex items-center justify-center p-8"
      style={{ background: CREAM, color: CHARCOAL }}
      data-nav-theme="light"
    >
      <div className="max-w-7xl w-full grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-16">
        {/* LEFT COLUMN */}
        <div className="flex flex-col justify-center">
          <h2
            className="font-serif m-0 max-w-[560px] text-h2 antialiased leading-[1.1] tracking-[-0.01em]"
            // style={{ fontSize: 'clamp(2.4rem, 5vw, 4.2rem)', color: CHARCOAL }}
          >
            {(
              [
                { text: 'Ready', gold: false },
                { text: 'to', gold: false },
                { text: 'transform', gold: false },
                { text: 'your', gold: false },
                { text: 'home', gold: false },
                { text: 'into', gold: false },
                { text: 'something', gold: true },
                { text: '\u00A0', gold: false },
                { text: 'extraordinary', gold: true },
              ] as { text: string; gold: boolean }[]
            ).map(({ text, gold }, i) =>
              text === '\u00A0' ? (
                <span
                  key={i}
                  className="ft-word block h-0 w-0 leading-none"
                  aria-hidden
                />
              ) : (
                <span
                  key={i}
                  className={`ft-word mr-[0.20em] inline-block`}
                  style={gold ? { color: GOLD_LIGHT } : undefined}
                >
                  {text}
                </span>
              )
            )}
            <span className={`ft-word -ml-[0.08em] inline-block`}>?</span>
          </h2>

          <p className="text-neutral-dark text-h3 font-sans font-extralight leading-relaxed my-2 md:my-12">
            Tell us about your vision and we'll reach out to get started.
          </p>
        </div>

        {/* RIGHT COLUMN — FORM or SUCCESS */}
        <div
          ref={ref}
          className="bg-[#e8e2d9] shadow-sm border border-[#b89a5e]/20 p-8 relative"
        >
          <div
            ref={successRef}
            className="absolute inset-0 flex items-center justify-center p-8"
            style={{
              opacity: 0,
              pointerEvents: submittedData ? 'auto' : 'none',
            }}
          >
            <p className="font-sans font-extralight text-[#1c1a17] text-lg leading-relaxed text-center">
              Thank you for reaching out to us! We will be in touch soon to
              discuss your project further.
            </p>
          </div>
          <form
            ref={formElRef}
            aria-hidden={!!submittedData}
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            name="contact"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
          >
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden" style={{ display: 'none' }}>
              <label>
                Don't fill this out: <input {...register('bot-field')} />
              </label>
            </p>

            {/* First + Last */}
            <div className="grid grid-cols-2 gap-4 ">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
                >
                  First Name
                  <span className="text-[#1c1a17] font-sans font-extralight">
                    {' '}
                    *
                  </span>
                </label>
                <InputField
                  id="firstName"
                  type="text"
                  placeholder="John"
                  error={errors.firstName?.message}
                  {...register('firstName')}
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
                >
                  Last Name{' '}
                  <span className="text-[#1c1a17] font-sans font-extralight">
                    {' '}
                    *
                  </span>
                </label>
                <InputField
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  error={errors.lastName?.message}
                  {...register('lastName')}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
              >
                Email{' '}
                <span className="text-[#1c1a17] font-sans font-extralight">
                  {' '}
                  *
                </span>
              </label>
              <InputField
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
              >
                Phone *
              </label>
              <InputField
                id="phone"
                type="tel"
                placeholder="123-456-7890"
                error={errors.phone?.message}
                {...register('phone', {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/[^\d\-()\s]/g, '');
                  },
                })}
              />
            </div>

            {/* What to build */}
            <div>
              <label
                htmlFor="whatToBuild"
                className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
              >
                What do you want to build/remodel?
                <span className="text-[#1c1a17] font-sans font-extralight">
                  {' '}
                  *
                </span>
              </label>
              <InputField
                id="whatToBuild"
                type="textarea"
                placeholder="Tell us about your project..."
                error={errors.whatToBuild?.message}
                {...register('whatToBuild')}
              />
            </div>

            {/* How did you hear */}
            <div>
              <label
                htmlFor="howDidYouHearAboutUs"
                className="block text-sm tracking-wider font-sans font-extralight text-[#1c1a17] mb-1.5"
              >
                How did you hear about us?
                <span className="text-[#1c1a17] font-sans font-extralight">
                  {' '}
                  *
                </span>
              </label>
              <InputField
                id="howDidYouHearAboutUs"
                type="textarea"
                placeholder="Referral, Google, social media..."
                error={errors.howDidYouHearAboutUs?.message}
                {...register('howDidYouHearAboutUs')}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full uppercase bg-[#00391F] hover:bg-[#004D29] text-neutral tracking-[4px] font-sans text-base font-light py-3 px-10 transition-colors duration-200 mt-2 active:bg-neutral-dark"
            >
              {isSubmitting ? 'Sending...' : `Let's Connect`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
